import { Alert, Button, ButtonGroup, Tooltip } from '@mui/material';
import { useDraftsBulkSend } from 'components/Requests/state/drafts-bulk-send-state';
import {
  usePostApiV5FirmsWorkflowRequestsBulkDispatch,
  postApiV5FirmsWorkflowRequestsBulkDispatch,
} from 'api/v5/firms-requests/firms-requests';
import { AlertMessage } from 'utilities/utils';
import { TOAST_FAILURE, TOAST_SUCCESS } from 'constants/constants';
import { useTrackRequestEvent } from 'Mixpanel/useTrackRequestEvent';

type DraftsBulkSendPreviewFooterProps = {
  message?: string | null;
  requiresDueDate?: boolean;
  disabled?: boolean;
};

export const DraftsBulkSendPreviewFooter = ({
  message,
  requiresDueDate = true,
  disabled = false,
}: DraftsBulkSendPreviewFooterProps) => {
  const {
    selectedDraftIds,
    getSelectedDraftsCount,
    removeAllSelectedDrafts,
    dueDate,
    setDueDate,
  } = useDraftsBulkSend();
  const { trackRequestEvent } = useTrackRequestEvent();

  const selectedFileCount = getSelectedDraftsCount();

  const { mutate: bulkSendMutate, isLoading } =
    usePostApiV5FirmsWorkflowRequestsBulkDispatch({
      mutation: {
        mutationFn: (values) =>
          postApiV5FirmsWorkflowRequestsBulkDispatch(values.data).then(
            (response) => {
              const hasErrors = Number(response?.data?.errors?.length) > 0;

              if (hasErrors) {
                throw new Error('Some drafts were not sent');
              }

              return response;
            },
          ),
        onSuccess: () => {
          // Track request_sent events
          selectedDraftIds.forEach((draftId) => {
            trackRequestEvent('request_sent', {
              // NOTE: We don't have a lot of data for these requests here
              request_id: draftId,
              due_date: dueDate,
            });
          });

          AlertMessage(TOAST_SUCCESS, 'Drafts sent successfully');
          removeAllSelectedDrafts();
          setDueDate('');
        },
        onError: () => {
          AlertMessage(TOAST_FAILURE, 'Failed to send drafts');
        },
      },
    });

  const handleBulkSendClick = () => {
    bulkSendMutate({
      data: {
        request: {
          ids: selectedDraftIds,
          due_date: dueDate,
        },
      },
    });
  };

  const handleClearSelectedClick = () => {
    removeAllSelectedDrafts();
  };

  const isSendDisabled =
    selectedFileCount === 0 || (requiresDueDate && !dueDate);

  return (
    <>
      <ButtonGroup
        variant="contained"
        disableElevation
        disabled={disabled || isLoading}
      >
        <Tooltip
          title={
            dueDate || !requiresDueDate
              ? ''
              : 'Please select a due date to enable Bulk Send'
          }
        >
          <span>
            <Button onClick={handleBulkSendClick} disabled={isSendDisabled}>
              Bulk Send {selectedFileCount} items
            </Button>
          </span>
        </Tooltip>
        <Button onClick={handleClearSelectedClick} color="secondary">
          Clear Selected
        </Button>
      </ButtonGroup>
      {message && (
        <Alert severity="info" style={{ padding: '0px 0px', lineHeight: 1.7 }}>
          {message}
        </Alert>
      )}
    </>
  );
};
